import {
  ConfigApi,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

import type { CustomMetricsApi } from './CustomMetricsApi';
import type {
  CatalogMetricsResponse,
  CustomMetricsQueryParams,
  HeadlinesMetricsResponse,
  OverviewMetricsResponse,
  SearchMetricsResponse,
  TemplatesMetricsResponse,
  UsersMetricsResponse,
} from './types';

function buildQuery(params: CustomMetricsQueryParams): string {
  const q = new URLSearchParams({
    from: params.from,
    to: params.to,
    userGroup: params.userGroup,
  });
  return q.toString();
}

async function readErrorMessage(resp: Response): Promise<string> {
  const text = await resp.text();
  if (!text) {
    return `HTTP ${resp.status}`;
  }
  try {
    const data = JSON.parse(text) as { message?: string };
    if (typeof data.message === 'string') {
      return data.message;
    }
  } catch {
    // not JSON
  }
  return text;
}

/**
 * HTTP client: `GET {base}/{resource}?from&to&userGroup` where `base` is either
 * `app.customMetrics.baseUrl` or discovery target `custom-metrics`.
 */
export class CustomMetricsClient implements CustomMetricsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly configApi: ConfigApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    configApi: ConfigApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.configApi = options.configApi;
  }

  private async resolveBaseUrl(): Promise<string> {
    const configured = this.configApi
      .getOptionalString('app.customMetrics.baseUrl')
      ?.trim();
    if (configured) {
      return configured.replace(/\/$/, '');
    }
    const discovered = await this.discoveryApi.getBaseUrl('custom-metrics');
    return discovered.replace(/\/$/, '');
  }

  private async getJson<T>(resource: string, params: CustomMetricsQueryParams): Promise<T> {
    const base = await this.resolveBaseUrl();
    const qs = buildQuery(params);
    const url = `${base}/${resource.replace(/^\//, '')}?${qs}`;
    const resp = await this.fetchApi.fetch(url);

    if (!resp.ok) {
      const message = await readErrorMessage(resp);
      throw new Error(message);
    }

    return (await resp.json()) as T;
  }

  getCatalog(params: CustomMetricsQueryParams) {
    return this.getJson<CatalogMetricsResponse>('catalog', params);
  }

  getHeadlines(params: CustomMetricsQueryParams) {
    return this.getJson<HeadlinesMetricsResponse>('headlines', params);
  }

  getOverview(params: CustomMetricsQueryParams) {
    return this.getJson<OverviewMetricsResponse>('overview', params);
  }

  getSearch(params: CustomMetricsQueryParams) {
    return this.getJson<SearchMetricsResponse>('search', params);
  }

  getTemplates(params: CustomMetricsQueryParams) {
    return this.getJson<TemplatesMetricsResponse>('templates', params);
  }

  getUsers(params: CustomMetricsQueryParams) {
    return this.getJson<UsersMetricsResponse>('users', params);
  }
}
